import {Button} from 'antd'
import {event_add_Api, event_getEventsByProps_Api} from 'apis/event'
import {getId, getType} from 'apis/model/base'
import ApiInfoForm from 'components/form/ApiInfoForm'
import getLinkToDetail from 'helpers/getLinkToDetail'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useMemo, useRef, useState} from 'react'
import {compose, fromRenderProps} from 'recompose'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {modalWidth} from 'views/AppLocals/configs'
import {NullAsDefault} from 'views/Shared'
import WorkspaceContext from '../WorkspaceContext'
import usePaginationHelper, {createPagerKey} from "../../AppLocals/hooks/usePaginationHelper";
import {useRouteSearchParam} from "../../../envs/_current/history";
import {renderEventCompactItem} from "../../AppLocals/Event/components/EventItem";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import createContextMenu from "../../AppLocals/factory/createContextMenu";
import {BsGear} from "react-icons/bs";
import CRUD from "../../Shared/enums/CRUD";
import {BiTrash} from "react-icons/bi";
import {showDeleteConfirmDialog} from "../../AppLocals/factory/createConfirmDialog";
import {useEventApi} from "../../AppLocals/Event/functions/useEventApi";
import Modal from "../../../components/Modal/Modal";
import CreateEventForm from "../../Event/CreateEventForm";
import dayjs from "dayjs";
import Translate from "../../../modules/local/Translate";

const WorkspaceCreateEventModal = ({open, owner_id, owner_type, owner_title, onCancel, onSuccess}) => {
    if (!open) return null
    return (
        <Modal
            open={open}
            footer={null}
            maskClosable={false}
            onCancel={onCancel}
            width={modalWidth.medium}
            styles={{body: {padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px'}}}>
            <Translate>
                {(t) => (
                    <ApiInfoForm
                            onSuccess={onSuccess}
                            prefixStr="WorkspaceCreateEvent"
                            apiInfo={event_add_Api}
                            validate={({default_owner_id, ...values}) => {
                                let errors = {}
                                if (values.currentIndex === 1) {
                                    if (_.isEmpty(_.get(values, 'title'))) {
                                        errors = {...errors, title: t('required')}
                                    }
                                    if (_.get(values, 'title.length') < 10) {
                                        errors = {
                                            ...errors,
                                            title: t('name required more than {1} characters', {1: 10}),
                                        }
                                    }
                                }
                                return errors
                            }}
                            onPreSubmit={({default_owner_id, default_owner_type, ...values}) => {
                                let _values = values
                                if (!!values.cohosts) {
                                    _values = {
                                        ..._values,
                                        cohosts: JSON.stringify(
                                            values.cohosts.map((e) => ({type: e._type, id: e.id}))
                                        ),
                                    }
                                }
                                if (
                                    !!values.start_date && dayjs.isDayjs(values.start_date) &&
                                    !!values.start_time && dayjs.isDayjs(values.start_time)
                                ) {
                                    _values = {
                                        ..._values,
                                        time_open: dayjs(
                                            values.start_date.format('YYYY-MM-DD') + ' ' + values.start_time.format('HH:mm')
                                        ).utc().format(),
                                    }
                                }
                                if (
                                    !!values.end_date && dayjs.isDayjs(values.end_date) &&
                                    !!values.end_time && dayjs.isDayjs(values.end_time)
                                ) {
                                    _values = {
                                        ..._values,
                                        time_close: dayjs(
                                            values.end_date.format('YYYY-MM-DD') + ' ' + values.end_time.format('HH:mm')
                                        ).utc().format(),
                                    }
                                }
                                return _values
                            }}
                            initialValues={{
                                owner_id,
                                owner_type,
                                owner_title,
                                default_owner_id: owner_id,
                                privacy: 1,
                                event_type: 'meeting',
                                start_date: dayjs(),
                                start_time: dayjs(),
                            }}>
                            {(props) => <CreateEventForm {...props}/>}
                    </ApiInfoForm>
                )}
            </Translate>
        </Modal>
    )
}

const WorkspaceEventList = ({
                                owner_id, owner_type, owner_title,
                                Wrapper = 'div',
                                filter_actions = {
                                    sortBy: 'sort-by',
                                    keyword: 'keyword',
                                },
                            }) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const history = useHistory()

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const keyword = useRouteSearchParam(filter_actions.keyword)
    const [refreshToken, setRefreshToken] = useState(0)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

    const refreshKey = useMemo(() => {
        return [owner_id, owner_type, sortBy, keyword, refreshToken].join('/')
    }, [owner_id, owner_type, sortBy, keyword, refreshToken])

    const {onDelete} = useEventApi({
        notifyListeners: {
            onDeleted: () => setRefreshToken(prev => prev + 1),
        },
    })

    const handleActionRef = useRef()
    handleActionRef.current = (action, item) => {
        switch (action) {
            case 'setting':
                history.push(getLinkToDetail(item) + '/settings')
                break
            case CRUD.DELETE:
                showDeleteConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onDelete({}, {id: getId(item)})
                    },
                })
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <BsGear size={14} className="text-blue-500"/>,
                        label: <span className="text-color-100">{t('setting')}</span>,
                        key: 'setting',
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: <span className="text-color-100">{t('delete')}</span>,
                        key: CRUD.DELETE,
                    },
                ].filter(() => item),
                (key) => handleActionRef.current(key, item)
            ),
        []
    )

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        renderFilter: NullAsDefault,
        AdvancedFilter: NullAsDefault,
        query_params: filter_actions,
        withKey: createPagerKey(refreshKey),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
            PaginationMeta: NullAsDefault,
            apiInfo: event_getEventsByProps_Api,
            renderItem: renderEventCompactItem({
                isLg: isLg,
                translate: t,
                renderContextMenu,
            }),
            renderWrap: (child) => (
                <div className="mt-4 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-6">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex flex-col flex-1">
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                            {t('event')}
                        </h2>
                        <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                    </div>
                    <Button
                        color="primary" variant="solid"
                        className="font-semibold rounded-lg no-border"
                        onClick={() => setIsCreateModalOpen(true)}>
                        {t('create')}
                    </Button>
                </div>
                {filterBar}
                {searchBar}
            </div>
        ),
    })

    return (
        <Wrapper className="flex flex-col gap-3">
            {pager_widget}
            <div className="h-16"/>
            <WorkspaceCreateEventModal
                open={isCreateModalOpen}
                owner_id={owner_id}
                owner_type={owner_type}
                owner_title={owner_title}
                onCancel={() => setIsCreateModalOpen(false)}
                onSuccess={() => {
                    setIsCreateModalOpen(false)
                    setRefreshToken(prev => prev + 1)
                }}
            />
        </Wrapper>
    )
}

export default compose(
    fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
        owner_id: getId(organization),
        owner_type: getType(organization),
        owner_title: organization?.title || '',
    }))
)(WorkspaceEventList)
